import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Box, Center, Spinner, SimpleGrid, Text, Badge } from '@chakra-ui/react';
import { TableOptions, DataTable, Pagination, ToggleOption, Column } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useForm, FormProvider, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import RoleForm from '../forms/RoleForm';
import CloneRoleDialog from '../forms/CloneRoleDialog';
import { Button as ButtonComponent } from '@themes/aegix';
import { RoleRow, RoleFormValues } from '../../types/role';
import { roleFormSchema } from '../../validations/role.validation';
import { GroupStatus } from '../../services';
import { useRolesContentData } from './hooks/useRolesContentData';

// Strip the `org_<public_id>_` prefix the backend adds to Group.name for
// uniqueness. We prefer the API's display_name when present, but fall back to
// stripping in case display_name is missing or itself returned prefixed (e.g.
// a custom role missing its RoleMetadata row).
const ROLE_NAME_PREFIX_RE = /^org_[A-Za-z0-9-]+_/;
const stripRolePrefix = (name: string): string =>
  name ? name.replace(ROLE_NAME_PREFIX_RE, '') : '';

export function RolesContent() {
  const { t } = useTranslation();

  const roleToggleOptions: ToggleOption[] = useMemo(() => [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'active', label: t('common.active', 'Active') },
    { value: 'system', label: t('roles.system', 'System') },
  ], [t]);

  const roleColumns: Column<RoleRow>[] = useMemo(() => [
    {
      key: 'display_name',
      label: t('roles.roleName', 'Role Name'),
      sortable: true,
      filterable: true,
      width: '220px',
    },
    {
      key: 'organization',
      label: t('common.organization', 'Organization'),
      sortable: true,
      filterable: true,
      width: '150px',
    },
    { key: 'members_count', label: t('roles.members', 'Members'), sortable: true, width: '150px' },
    {
      key: 'permissions_count',
      label: t('roles.permissions', 'Permissions'),
      width: '150px',
    },
    {
      key: 'is_system_role',
      label: t('common.type', 'Type'),
      filterable: true,
      filterOptions: [
        { label: t('roles.system', 'System'), value: 'true' },
        { label: t('roles.custom', 'Custom'), value: 'false' },
      ],
      width: '100px',
      render: (row: RoleRow) =>
        React.createElement(
          Badge,
          {
            colorPalette: row.is_system_role ? 'blue' : 'green',
            size: 'sm',
            variant: 'subtle',
          },
          row.is_system_role ? t('roles.system', 'System') : t('roles.custom', 'Custom'),
        ),
    },
  ], [t]);

  const {
    drawerOpen,
    actionMode,
    currentRow,
    cloneDialogOpen,
    roleToClone,
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    filterValues,
    roles,
    total,
    totalPages,
    isLoading,
    error,
    handleFilterChange,
    handleFilterClear,
    handleSort,
    openAddDrawer,
    openEditDrawer,
    closeDrawer,
    openCloneDialog,
    closeCloneDialog,
    handleSubmit,
    handleDelete,
  } = useRolesContentData();

  const methods = useForm<RoleFormValues>({
    defaultValues: {
      name: '',
      display_name: '',
      organization: '',
      organization_id: '',
      organization_name: null,
      description: '',
      permissions: [],
      permission_codenames: [],
      user_count: 0,
      members_count: 0,
      permissions_count: 0,
      is_system_role: false,
      status: 'active',
    },
    resolver: zodResolver(roleFormSchema) as unknown as Resolver<RoleFormValues>,
  });

  // enableReinitialize equivalent
  useEffect(() => {
    if (currentRow) {
      methods.reset({
        name: stripRolePrefix(
          currentRow.display_name || currentRow.name || '',
        ),
        display_name: stripRolePrefix(currentRow.display_name ?? ''),
        organization: currentRow.organization ?? '',
        organization_id: currentRow.organization_id
          ? String(currentRow.organization_id)
          : '',
        organization_name: currentRow.organization ?? null,
        description: currentRow.description ?? '',
        permissions: currentRow.permissions ?? [],
        permission_codenames: currentRow.permission_codenames ?? [],
        user_count: currentRow.members_count ?? 0,
        members_count: currentRow.members_count ?? 0,
        permissions_count: currentRow.permissions_count ?? 0,
        is_system_role: currentRow.is_system_role ?? false,
        status: currentRow.status ?? 'active',
      });
    } else {
      methods.reset({
        name: '',
        display_name: '',
        organization: '',
        organization_id: '',
        organization_name: null,
        description: '',
        permissions: [],
        permission_codenames: [],
        user_count: 0,
        members_count: 0,
        permissions_count: 0,
        is_system_role: false,
        status: 'active',
      });
    }
  }, [currentRow, methods]);

  return (
    <>
      <TableOptions
        toggleOptions={roleToggleOptions}
        activeToggle={statusFilter}
        onToggleChange={(v: string) => {
          setStatusFilter(v as GroupStatus);
          setCurrentPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
        searchPlaceholder={t('common.searchPlaceholder', 'Search...')}
        primaryAction={{
          label: t('roles.createRole', 'Create Role'),
          onClick: openAddDrawer,
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : t('roles.failedLoad', 'Failed to load roles')}
            </Text>
          </Center>
        ) : roles.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('roles.noRolesFound', 'No roles found')}</Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={roleColumns}
              data={roles}
              keyExtractor={(row: RoleRow) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={openEditDrawer}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={total}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </Box>
      <DrawerComponent
        isOpen={drawerOpen}
        onClose={closeDrawer}
        title={actionMode === 'edit' ? t('roles.editRole', 'Edit Role') : t('roles.createRole', 'Create Role')}
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <SimpleGrid
            columns={
              actionMode === 'edit' ? (currentRow?.is_system_role ? 2 : 4) : 2
            }
            gap={4}
            width={'100%'}
          >
            {actionMode === 'edit' && !currentRow?.is_system_role && (
              <ButtonComponent visual="alert" onClick={handleDelete} data-e2e="delete-button">
                {t('common.delete', 'Delete')}
              </ButtonComponent>
            )}
            {actionMode === 'edit' && (
              <ButtonComponent
                visual="secondary"
                onClick={() => currentRow && openCloneDialog(currentRow)}
               data-e2e="clone-button">
                {t('roles.clone', 'Clone')}
              </ButtonComponent>
            )}
            <ButtonComponent visual="secondary" onClick={closeDrawer} data-e2e="cancel-button">
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            {!currentRow?.is_system_role && (
              <ButtonComponent visual="primary" type="submit" form="role-form" data-e2e="save-button">
                {t('common.save', 'Save')}
              </ButtonComponent>
            )}
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form
            id="role-form"
            onSubmit={methods.handleSubmit((values) =>
              handleSubmit(values, methods),
            )}
          >
            <RoleForm isSystemRole={currentRow?.is_system_role ?? false} />
          </form>
        </FormProvider>
      </DrawerComponent>

      <CloneRoleDialog
        isOpen={cloneDialogOpen}
        onClose={closeCloneDialog}
        sourceRole={roleToClone}
      />
    </>
  );
}
